// NavarchAI theme system — PURELY VISUAL, from the "NavarchAI Home" mockup.
//
// Injected AFTER the legacy DYNAMIC_STYLES stylesheet in App.jsx, so these
// rules win the cascade and re-skin every screen (home, chat, manuals, SIRE,
// auth, plans, history, admin) without touching markup or logic.
//
// TWO full token sets on the SAME variable names the whole app already uses:
//   :root                → LIGHT (the default theme)
//   [data-theme='dark']  → DARK (the mockup's primary look)
// The <html data-theme> attribute is driven by src/theme/useTheme.js:
// saved choice ('navarch-theme' in localStorage) → else the device's
// prefers-color-scheme → else light.
//
// WCAG AA notes (deliberate deviations from the raw mockup values):
//   - dark --text-3: mockup #56688c is ~3.9:1 on #060b16 — bumped to #8598bd
//     (~6:1). The original stays available as --text-faint for decoration.
//   - light --text-3: mockup #7684a4 is ~3.8:1 on #eef3fa — bumped to #5f6f92
//     (~4.7:1); original kept as --text-faint.
//   - light --red: #dc2626 is ~4.0:1 on #eef3fa — darkened to #b91c1c (~5.3:1).
//
// Fonts come from public/index.html (Space Grotesk + JetBrains Mono, both
// with display=swap). The stacks below always include system fallbacks so
// the app renders fine on a ship with no connectivity.
export const NAVARCH_THEME = `
:root {
  /* ---- LIGHT (default) ---- */
  --bg: #eef3fa;
  --page-bg: #eef3fa;
  --bg-rad1: rgba(14, 116, 144, 0.08);
  --bg-rad2: rgba(217, 119, 6, 0.07);
  --orb1: rgba(56, 189, 248, 0.18);
  --orb2: rgba(245, 166, 35, 0.14);

  --surface-1: #ffffff;
  --surface-2: #ffffff;
  --surface-3: #e9eef7;
  --border: rgba(22, 33, 59, 0.14);
  --border-soft: rgba(22, 33, 59, 0.08);
  --hairline: rgba(22, 33, 59, 0.08);

  --text: #16213b;
  --text-2: #4a5a7d;
  --text-3: #5f6f92;
  --text-faint: #7684a4;

  --primary: #16213b;
  --on-primary: #ffffff;
  --accent: #0e7490;

  --amber: #b45309;
  --amber-text: #b45309;
  --amber-soft: rgba(180, 83, 9, 0.08);
  --amber-line: rgba(180, 83, 9, 0.3);
  --cyan: #0e7490;
  --cyan-soft: rgba(14, 116, 144, 0.07);
  --cyan-line: rgba(14, 116, 144, 0.25);
  --green: #047857;
  --green-soft: rgba(4, 120, 87, 0.07);
  --green-line: rgba(4, 120, 87, 0.3);
  --red: #b91c1c;
  --yellow: #a16207;

  --header-bg: rgba(255, 255, 255, 0.8);
  --btn-bg: #ffffff;
  --btn-border: rgba(22, 33, 59, 0.14);
  --btn-color: #4a5a7d;
  --headline-grad: linear-gradient(92deg, #d97706 10%, #b45309 40%, #0e7490 90%);
  --section-line: rgba(22, 33, 59, 0.15);

  --card-bg: #ffffff;
  --card-line: rgba(22, 33, 59, 0.1);
  --card-shadow: 0 12px 32px -20px rgba(22, 33, 59, 0.25);
  --card-hov-shadow: 0 24px 50px -22px rgba(22, 33, 59, 0.35);
  --shadow-card: var(--card-shadow);
  --shadow-shell: none;

  --prompt-bg: rgba(255, 255, 255, 0.85);
  --prompt-border: rgba(22, 33, 59, 0.1);
  --prompt-shadow: 0 30px 70px -30px rgba(22, 33, 59, 0.45);
  --input-bg: #ffffff;
  --send-bg: #16213b;
  --send-color: #ffffff;
  --send-shadow: 0 6px 18px -6px rgba(22, 33, 59, 0.45);
  --chip-bg: #ffffff;
  --chip-color: #4a5a7d;
  --account-bg: #16213b;
  --account-border: #16213b;
  --account-color: #ffffff;
  --upgrade-grad: linear-gradient(135deg, #f5a623, #e8890c);
  --upgrade-color: #241503;
  --upgrade-shadow: 0 6px 20px -8px rgba(217, 119, 6, 0.5);

  --logo-tile: linear-gradient(135deg, #0f2440, #16213b);
  --logo-tile-border: rgba(14, 116, 144, 0.35);
  --logo-stroke: #ffffff;
  --logo-star: #c9a227;

  --mono: 'JetBrains Mono', 'SFMono-Regular', Consolas, monospace;
  --sans: 'Space Grotesk', -apple-system, 'Segoe UI', system-ui, Roboto, sans-serif;
}

[data-theme='dark'] {
  --bg: #060b16;
  --page-bg: #060b16;
  --bg-rad1: rgba(56, 189, 248, 0.09);
  --bg-rad2: rgba(245, 166, 35, 0.07);
  --orb1: rgba(56, 189, 248, 0.12);
  --orb2: rgba(245, 166, 35, 0.09);

  --surface-1: #0b1424;
  --surface-2: #0e1830;
  --surface-3: #17233d;
  --border: rgba(120, 150, 205, 0.2);
  --border-soft: rgba(120, 150, 205, 0.12);
  --hairline: rgba(120, 150, 205, 0.12);

  --text: #e8eef9;
  --text-2: #a9bad8;
  --text-3: #8598bd;
  --text-faint: #56688c;

  --primary: #1e3a6d;
  --on-primary: #ffffff;
  --accent: #7dd3fc;

  --amber: #f5a623;
  --amber-text: #fbbf24;
  --amber-soft: rgba(245, 166, 35, 0.1);
  --amber-line: rgba(245, 166, 35, 0.3);
  --cyan: #7dd3fc;
  --cyan-soft: rgba(56, 189, 248, 0.1);
  --cyan-line: rgba(56, 189, 248, 0.3);
  --green: #34d399;
  --green-soft: rgba(52, 211, 153, 0.1);
  --green-line: rgba(52, 211, 153, 0.3);
  --red: #f87171;
  --yellow: #fbbf24;

  --header-bg: rgba(6, 11, 22, 0.72);
  --btn-bg: rgba(255, 255, 255, 0.04);
  --btn-border: rgba(120, 150, 205, 0.2);
  --btn-color: #a9bad8;
  --headline-grad: linear-gradient(92deg, #f5a623 10%, #fbbf24 40%, #38bdf8 90%);
  --section-line: rgba(120, 150, 205, 0.25);

  --card-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.015) 55%);
  --card-line: rgba(120, 150, 205, 0.18);
  --card-shadow: 0 0 0 rgba(0, 0, 0, 0);
  --card-hov-shadow: 0 24px 50px -24px rgba(0, 0, 0, 0.7);
  --shadow-card: var(--card-shadow);
  --shadow-shell: none;

  --prompt-bg: rgba(10, 17, 32, 0.82);
  --prompt-border: rgba(120, 150, 205, 0.18);
  --prompt-shadow: 0 30px 70px -30px rgba(0, 0, 0, 0.8);
  --input-bg: rgba(255, 255, 255, 0.04);
  --send-bg: linear-gradient(135deg, #38bdf8, #0ea5e9);
  --send-color: #04121f;
  --send-shadow: 0 6px 18px -6px rgba(56, 189, 248, 0.55);
  --chip-bg: rgba(255, 255, 255, 0.05);
  --chip-color: #cdd9ee;
  --account-bg: rgba(56, 189, 248, 0.1);
  --account-border: rgba(56, 189, 248, 0.3);
  --account-color: #7dd3fc;

  --logo-tile: linear-gradient(135deg, #0e2036, #091426);
  --logo-tile-border: rgba(56, 189, 248, 0.35);
}

/* ---- global reset & overflow guards ----------------------------------------
   The app had no reset: the browser's default 8px body margin plus
   content-box sizing made every "width:100% + padding" container ~20px wider
   than the phone screen (hidden behind .app-root's overflow:clip, so content
   was silently cut at the right edge instead of scrolling). border-box +
   margin:0 fix the cause; the html/body clip is only the last-resort net.
   overflow:clip (not hidden) so position:sticky keeps working. */
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; max-width: 100%; overflow-x: clip; }
img, svg, video, canvas { max-width: 100%; height: auto; }

/* ---- animations (mockup) --------------------------------------------------- */
@keyframes navDrift1 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(60px, 40px) scale(1.15); } }
@keyframes navDrift2 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-70px, -30px) scale(1.1); } }
@keyframes navRiseIn { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
@keyframes navSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes navWave { from { background-position-x: 0; } to { background-position-x: 220px; } }

/* ---- base / shell ----------------------------------------------------------- */
body {
  font-family: var(--sans);
  background: var(--bg);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  transition: background 0.35s, color 0.35s;
}
::placeholder { color: var(--text-3); }
.app-root {
  background: var(--bg);
  background-image:
    radial-gradient(1100px 520px at 78% -8%, var(--bg-rad1), transparent 60%),
    radial-gradient(800px 480px at 8% 4%, var(--bg-rad2), transparent 55%);
  background-attachment: fixed;
  color: var(--text);
  padding: 0;
  min-height: 100vh;
  position: relative;
  overflow: clip;
  transition: background 0.35s, color 0.35s;
}
/* ambient orbs — pure CSS, no extra DOM */
.app-root::before,
.app-root::after {
  content: '';
  position: fixed;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(30px);
  z-index: 0;
}
.app-root::before {
  top: -180px; right: -120px; width: 520px; height: 520px;
  background: radial-gradient(circle, var(--orb1), transparent 65%);
  animation: navDrift1 16s ease-in-out infinite;
}
.app-root::after {
  bottom: -200px; left: -140px; width: 560px; height: 560px;
  background: radial-gradient(circle, var(--orb2), transparent 65%);
  animation: navDrift2 20s ease-in-out infinite;
}
.app-shell {
  max-width: none;
  margin: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}
.main-content { flex: 1; width: 100%; max-width: 1080px; margin: 0 auto; padding: 0 28px 90px; }

/* ---- header ------------------------------------------------------------------ */
.app-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--header-bg);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--hairline);
  padding: 14px 32px;
  transition: background 0.35s;
}
.brand-mark {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--logo-tile);
  border: 1px solid var(--logo-tile-border);
  position: relative;
  overflow: hidden;
}
.brand-mark::before {
  content: '';
  position: absolute;
  inset: 0;
  background: conic-gradient(from 0deg, transparent 0deg, rgba(125, 211, 252, 0.4) 40deg, transparent 80deg);
  animation: navSweep 5s linear infinite;
}
.brand-mark svg { position: relative; }
.brand-name { color: var(--text); font-size: 18px; letter-spacing: 0.3px; }
.brand-name em { color: var(--amber-text); }
.brand-sub { color: var(--text-3); font-size: 9.5px; letter-spacing: 2.2px; }
.status-pill {
  background: var(--green-soft);
  border: 1px solid var(--green-line);
  color: var(--green);
  letter-spacing: 1.4px;
}
.pulse-dot { background: var(--green); }
.theme-toggle {
  background: var(--btn-bg);
  border: 1px solid var(--btn-border);
  color: var(--btn-color);
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 12px;
  font-family: var(--mono);
  letter-spacing: 1px;
  cursor: pointer;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.35s, color 0.35s, border-color 0.2s;
}
.theme-toggle:hover { border-color: var(--cyan); color: var(--cyan); }
.header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
/* LOG IN / account + MANUALS chips (moved from inline styles so the mobile
   media queries below can compact them). Same visuals as before. */
.header-chip {
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 11.5px;
  font-family: var(--mono);
  letter-spacing: 1px;
  cursor: pointer;
  white-space: nowrap;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.account-chip { background: var(--primary, #16192b); border: none; color: var(--on-primary, #ffffff); }
.manuals-chip { background: var(--surface-1, #ffffff); border: 1px solid var(--border); color: var(--text-2); }

/* ---- hero --------------------------------------------------------------------- */
.hero-section { padding: 68px 12px 52px; animation: navRiseIn 0.7s ease-out both; }
.hero-kicker {
  color: var(--cyan);
  background: var(--cyan-soft);
  border: 1px solid var(--cyan-line);
  letter-spacing: 2.6px;
  padding: 8px 18px;
  margin-bottom: 28px;
}
.hero-title { color: var(--text); font-size: clamp(32px, 5vw, 54px); font-weight: 700; letter-spacing: -1.2px; line-height: 1.12; overflow-wrap: break-word; }
.hero-title .hero-name { color: var(--text-3); font-weight: 600; }
.hero-title .grad {
  background: var(--headline-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.hero-sub { color: var(--text-2); font-size: 17px; max-width: 540px; }
.hero-meta { display: none; } /* the NavarchAI mockup has no stats row */
.section-label { color: var(--text-3); letter-spacing: 3px; }
.section-label::after { background: linear-gradient(90deg, var(--section-line), transparent); }

/* ---- cards --------------------------------------------------------------------- */
/* min(100%, …) so the track can never demand more than the viewport offers */
.category-grid { grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr)); padding-bottom: 8px; }
.cat-card,
.select-card,
.panel {
  background: var(--card-bg);
  border: 1px solid var(--card-line);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s, background 0.35s;
}
.cat-card { padding: 28px 24px; animation: navRiseIn 0.6s ease-out both; }
.cat-card::before { display: none; }
.cat-card:hover,
.select-card:hover {
  transform: translateY(-5px);
  border-color: var(--card-accent, var(--cyan-line));
  box-shadow: var(--card-hov-shadow);
}
.cat-icon { border-radius: 10px; width: 42px; height: 42px; }
.cat-title { color: var(--text); font-size: 18px; margin-bottom: 7px; }
.cat-desc { color: var(--text-2); font-size: 13.5px; line-height: 1.6; }
.cat-foot { color: var(--text-3); }
.cat-badge {
  color: var(--green);
  background: var(--green-soft);
  border: 1px solid var(--green-line);
  letter-spacing: 1.2px;
}
.panel-label { color: var(--text-3); }
.sc-title { color: var(--card-accent, var(--accent)); }
.sc-sub { color: var(--text-3); }

/* ---- buttons & inputs ------------------------------------------------------------ */
.btn { border-radius: 999px; min-height: 44px; }
.btn-ghost {
  background: var(--btn-bg);
  border: 1px solid var(--btn-border);
  color: var(--btn-color);
}
.btn-ghost:hover { border-color: var(--cyan); color: var(--cyan); filter: none; }
.text-input,
.input-field {
  background: var(--input-bg);
  border: 1px solid var(--btn-border);
  color: var(--text);
  border-radius: 14px;
}
.text-input::placeholder,
.input-field::placeholder { color: var(--text-3); }
.text-input:focus,
.input-field:focus { border-color: var(--cyan); }
.chip {
  background: var(--chip-bg);
  border: 1px solid var(--btn-border);
  color: var(--chip-color);
  min-height: 36px;
}
.chip:hover { border-color: var(--cyan-line); color: var(--cyan); background: var(--cyan-soft); }
.filter-btn { background: var(--btn-bg); border: 1px solid var(--btn-border); color: var(--btn-color); }
.filter-btn.active { background: var(--primary); border-color: var(--primary); color: var(--on-primary); }
.equip-change { background: var(--btn-bg); color: var(--btn-color); }

/* ---- stepper / equip bar / chat / checklist ---------------------------------------- */
.step .dot { background: var(--surface-3); color: var(--text-3); border-color: var(--border); }
.step.current .dot { background: var(--primary); color: var(--on-primary); border-color: var(--primary); }
.step .name { color: var(--text-3); }
.step.done .name { color: var(--text-2); }
.step.current .name { color: var(--text); }
.step-line { background: var(--border); }
.equip-bar { background: var(--surface-1); border-color: var(--border); border-radius: 18px; }
.equip-path { color: var(--text-3); }
.msg.insp .body { background: var(--surface-3); border-color: var(--border); color: var(--text); }
.msg.user .body { background: var(--primary); border-color: var(--primary); color: var(--on-primary); }
.bubble .who { color: var(--text-3); }
.avatar { border-color: var(--border); background: var(--surface-3); }
.q-alert { color: var(--yellow); background: var(--amber-soft); border-color: var(--amber-line); }
.cl-item { background: var(--surface-1); border-color: var(--border-soft); }
.cl-item:hover { border-color: var(--border); }
.cl-qn { color: var(--cyan); }
.cl-item.checked .cl-text { color: var(--text-3); }
.progress-track { background: var(--surface-3); }
.progress-fill { background: linear-gradient(90deg, var(--green), var(--cyan)); }
.score-pill.sp-pass { background: var(--green-soft); color: var(--green); }
.score-pill.sp-part { background: var(--amber-soft); color: var(--yellow); }
.score-pill.sp-fail { background: rgba(220, 38, 38, 0.1); color: var(--red); }
.score-pill.sp-skip { background: var(--surface-3); color: var(--text-3); } /* neutral */
.t-num { color: var(--card-accent, var(--accent)); }
.t-desc { color: var(--text-2); }
.disclaimer { color: var(--text-3); }

/* ---- long-word / URL wrapping guards ----------------------------------------
   Long URLs, part numbers and file names must wrap instead of painting past
   the container edge (rect checks don't catch painted text overflow). */
.main-content, [role='dialog'] { overflow-wrap: anywhere; } /* inherited by all content */
.hero-sub, .cat-title, .cat-desc, .sc-title, .sc-sub, .equip-name, .equip-path,
.panel, .cl-text, .cl-qn, .t-title, .t-desc, .q-alert, .prompt-meta,
.section-label { overflow-wrap: anywhere; }
.bubble { min-width: 0; }
.bubble .body, .chat-bubble-body { max-width: 100%; overflow-wrap: anywhere; }
.chat-bubble-body pre { max-width: 100%; overflow-x: auto; }
.chat-bubble-body table { max-width: none; } /* the wrapper scrolls, not the table */

/* ---- header pills (Upgrade) --------------------------------------------------------- */
.pill-primary {
  background: var(--upgrade-grad);
  color: var(--upgrade-color);
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 12.5px;
  font-weight: 700;
  font-family: var(--sans);
  letter-spacing: 0.3px;
  cursor: pointer;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: var(--upgrade-shadow);
  transition: filter 0.15s, transform 0.1s;
}
.pill-primary:hover { filter: brightness(1.08); }
.pill-primary:active { transform: scale(0.98); }

/* ---- home prompt bar ------------------------------------------------------------------ */
.prompt-wrap {
  position: sticky;
  bottom: calc(16px + env(safe-area-inset-bottom)); /* clear the iPhone home indicator */
  margin-top: 52px;
  z-index: 30;
  background: var(--prompt-bg);
  backdrop-filter: blur(18px);
  border: 1px solid var(--prompt-border);
  border-radius: 26px;
  padding: 14px;
  box-shadow: var(--prompt-shadow);
  animation: navRiseIn 0.7s ease-out 0.4s both;
  transition: background 0.35s;
}
.prompt-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: var(--text-2);
  padding: 0 14px 10px;
  flex-wrap: wrap;
}
.prompt-meta a,
.prompt-meta button.linkish {
  color: var(--amber-text);
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
  padding: 0;
}
.prompt-meta a:hover,
.prompt-meta button.linkish:hover { text-decoration: underline; }
.prompt-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--input-bg);
  border: 1px solid var(--btn-border);
  border-radius: 999px;
  box-shadow: none;
  padding: 8px 8px 8px 22px;
}
.prompt-bar input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  font-size: 16px; /* ≥16px — no iOS zoom */
  font-family: var(--sans);
  min-width: 0;
}
.prompt-bar input::placeholder { color: var(--text-3); }
.prompt-send {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: var(--send-bg);
  color: var(--send-color);
  font-size: 17px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--send-shadow);
}
.prompt-send:hover { filter: brightness(1.15); }
.prompt-chips { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; margin-top: 12px; }
.prompt-chip {
  background: var(--chip-bg);
  color: var(--chip-color);
  border: 1px solid var(--btn-border);
  border-radius: 999px;
  padding: 11px 18px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: var(--sans);
  cursor: pointer;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: border-color 0.2s, color 0.2s;
}
.prompt-chip:hover { border-color: var(--cyan-line); color: var(--cyan); filter: none; }

/* ---- footer ------------------------------------------------------------------------------ */
.site-footer {
  text-align: center;
  padding: 18px 16px calc(26px + env(safe-area-inset-bottom));
  font-size: 11.5px;
  color: var(--text-3);
  line-height: 1.7;
  position: relative;
}
.site-footer::after {
  content: '';
  display: block;
  height: 3px;
  margin-top: 22px;
  background: repeating-linear-gradient(90deg, var(--cyan-line) 0 40px, var(--amber-line) 40px 80px, var(--green-line) 80px 120px);
  background-size: 220px 3px;
  animation: navWave 6s linear infinite;
  opacity: 0.55;
}

/* ---- mobile (mockup uses 700px) ------------------------------------------------------------ */
@media (max-width: 700px) {
  .app-header { padding: 10px 14px; }
  .status-pill { display: none; } /* free header space, like the mockup's data-mq-hide */
  .main-content { padding: 0 14px calc(48px + env(safe-area-inset-bottom)); }
  .hero-section { padding: 40px 4px 34px; }
  .hero-title { font-size: clamp(24px, 7.6vw, 30px); } /* scales 320→400px instead of a fixed 30px */
  .hero-sub { font-size: 15px; padding: 0 6px; }
  /* On phones the sticky bar covered the cards while scrolling — sit in flow
     after them instead, so nothing is ever hidden behind it. */
  .prompt-wrap { position: static; margin-top: 36px; padding: 10px; border-radius: 20px; }
  .prompt-chips { gap: 6px; }
  .prompt-chip { padding: 10px 13px; font-size: 11.5px; }
  .theme-toggle { padding: 9px 13px; font-size: 10.5px; }
  .pill-primary { padding: 9px 13px; font-size: 10.5px; }
}

/* ---- compact one-row header on phones ------------------------------------------------------ */
@media (max-width: 480px) {
  .app-header { padding: 10px 12px; gap: 8px; }
  .header-actions { flex-wrap: nowrap; gap: 6px; min-width: 0; }
  .brand { gap: 8px; min-width: 0; overflow: hidden; }
  .brand > div { min-width: 0; }
  .brand-mark { width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; }
  .brand-name { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .brand-sub { display: none; }
  /* theme toggle + Upgrade become icon-only round buttons */
  .theme-toggle { width: 36px; height: 36px; min-height: 36px; padding: 0; justify-content: center; font-size: 15px; flex-shrink: 0; }
  .theme-toggle .theme-toggle-label { display: none; }
  .pill-primary { width: 36px; height: 36px; min-height: 36px; padding: 0; justify-content: center; font-size: 15px; flex-shrink: 0; }
  .pill-primary .pill-label { display: none; }
  /* MANUALS may shrink/ellipsize; LOG IN / account keeps its space */
  .header-chip { padding: 9px 11px; font-size: 10px; letter-spacing: 0.5px; min-height: 36px; }
  .manuals-chip { flex-shrink: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
  .account-chip { flex-shrink: 0; max-width: 138px; overflow: hidden; text-overflow: ellipsis; }
}

/* Very narrow phones: LOG IN / account wins the space race over Upgrade
   (Upgrade stays reachable via the prompt bar's "Unlock more with Pro Plan"). */
@media (max-width: 360px) {
  .pill-primary { display: none; }
}

/* ---- reduced motion -------------------------------------------------------------------------- */
@media (prefers-reduced-motion: reduce) {
  .app-root::before,
  .app-root::after,
  .brand-mark::before,
  .site-footer::after,
  .pulse-dot,
  .hero-section,
  .cat-card,
  .prompt-wrap {
    animation: none !important;
  }
  .cat-card,
  .select-card,
  .cat-card:hover,
  .select-card:hover { transform: none; transition: none; }
}
`;
